<template>
    <div class="input-color" :class="{ 'inline': inline }">
        <b-dropdown ref="dropdown" :variant="variant" :class="[button ? 'button': 'simple', `size-${size}`, {'block': block}]" no-caret lazy>
            <template #button-content>
                <input-color-preview class="dropdown-color-preview" :size="size" :color="color" :title="title" @click="onClick">
                    <slot v-if="!button">
                        <i v-if="color === 'default' || color === 'inherit'" class="fas fa-droplet-slash"/>
                    </slot>
                </input-color-preview>
                <slot v-if="button">
                    <div class="content">
                        <div class="form-label">{{ label|tr }}</div>
                        <div class="form-help">{{ colorComputed }}</div>
                    </div>
                </slot>
            </template>

            <div v-if="!noVariants" class="color-palette">
                <h5 v-tr>Theme|Thème</h5>
                <input-color-preview v-if="allowDefault" color="default" @click="setColor('default')" :selected="color === 'default'"
                                     :title="'By default|Par défaut'|tr">
                    <i class="fas fa-droplet-slash text-black"/>
                </input-color-preview>
                <input-color-preview v-else-if="allowInherit" color="inherit" @click="setColor('inherit')" :selected="color === 'inherit'"
                                     :title="'Inherit|Hériter'|tr">
                    <i class="fas fa-droplet-slash text-black"/>
                </input-color-preview>
                <input-color-preview v-for="(c, key) in cmsWebsite.design.colors" :color="key" @click="setColor(key)" :selected="c === color || key === color"
                                     :title="key" :key="`preset-${c}`"/>
                <input-color-preview v-if="allowTransparent" color="transparent" @click="setColor('transparent')" :selected="color === 'transparent'"
                                     title="Transparent"/>
                <b-dropdown-divider/>
            </div>

            <div v-if="!noPresets" class="color-palette">
                <input-color-preview v-for="(c, index) in presets" :color="c" @click="color = c" :selected="c === color" :key="`color-${index}`" size="sm"/>
                <b-dropdown-divider/>
            </div>
            <b-dropdown-item @click="show">
                <i class="fas fa-palette"/>
                <span v-tr>More Colors|Plus de couleurs</span>
            </b-dropdown-item>
        </b-dropdown>

        <e-photoshop-color-picker v-if="pickerEnabled" :value="color" @input="onChanged" @ok="ok" @cancel="cancel" :disable-alpha="!allowTransparent"/>
    </div>
</template>

<script>
import InputColorPreview from "@/components/editor/properties/inputs/input-color-preview.vue";
import EPhotoshopColorPicker from "@vue-components/components/color-picker/e-photoshop-color-picker.vue";

// input-color #hot-reload-debug
export default {
    name: `input-color`,
    components: {EPhotoshopColorPicker, InputColorPreview},
    props: {
        value: {type: String},
        variant: {type: String, default: `white`},
        label: {type: String},
        size: {type: String, default: `md`},
        title: {type: String},
        block: {type: Boolean},
        useCssVar: {type: Boolean},
        forceColorValue: {type: Boolean}, // for WYSIWYG only
        inline: {type: Boolean},
        autoHide: {type: Boolean},
        allowTransparent: {type: Boolean}, // bg only
        allowInherit: {type: Boolean}, // glyph only
        allowDefault: {type: Boolean}, // text only
        button: {type: Boolean},
        noVariants: {type: Boolean},
        noPresets: {type: Boolean},
        noLivePreview: {type: Boolean},
        presets: {
            type: Array, default: () => [
                `#ff1616`,
                `#ff5983`,
                `#ff5810`,
                `#ff990a`,
                `#ffb90f`,
                `#ded222`,
                `#7bbd4b`,
                `#558632`,
                `#32864a`,
                `#3e96a5`,
                `#00aecc`,
                `#00c3e6`,
                `#0099f3`,
                `#006eff`,
                `#4058c5`,
                `#80428b`,
                `#8c52ff`,
                `#805140`,
                `#635454`,
                `#a49393`,
                `#eed6d3`,
                `#e8b4b8`,
                `#e3bc9a`,
                `#f3dbb2`,
                `#ffffff`,
                `#dddddd`,
                `#bbbbbb`,
                `#999999`,
                `#777777`,
                `#555555`,
                `#333333`,
                `#000000`
            ]
        },
    },
    data() {
        return {
            pickerEnabled: false,
            visible: false
        }
    },
    computed: {
        color: {
            get() {
                if (this.allowInherit && this.value === `inherit`) {
                    return this.value;
                }
                if (this.useCssVar && this.value && this.value.startsWith(`var(`)) {
                    return this.value.replace(`var(--color-`, ``).replace(`)`, ``);
                }
                return this.value;
            },
            set(value) {
                if (this.allowInherit && value === `inherit`) {
                    // don't change value here
                } else if (value && !value.startsWith(`#`)) {
                    if (this.forceColorValue) {
                        value = this.cmsWebsite.getColor(value);

                        // hex8
                        if (value.startsWith(`#`) && value.length === 9) {
                            value = value.substring(0, 7);
                        }
                    } else if (this.useCssVar) {
                        value = `var(--color-${value})`;
                    }
                }
                this.$emit(`input`, value);
                this.$emit(`change`, value);
            }
        },
        colorComputed() {
            return this.cmsWebsite.getColor(this.color);
        },
        style() {
            if (this.color === `default` || this.color === `inherit`) {
                return {background: `#000`};
            } else {
                return {background: this.color + ` !important`}
            }
        }
    },
    methods: {
        onClick() {
            this.$emit(`click`);
        },
        onChanged(evt) {
            if (!this.noLivePreview) {
                this.color = evt.hex8;
            }
        },
        setColor(color) {
            this.color = color;
            if (this.autoHide && this.$refs.dropdown) {
                this.$refs.dropdown.hide();
            }
        },
        show(color) {
            if (!this.color) {
                this.color = color;
            }
            this.$emit(`open-picker`);
            this.$nextTick(() => {
                this.pickerEnabled = true;
            });
            this.$emit(`show`);
        },
        ok(color) {
            this.close(color);
            this.$emit(`ok`, color);
        },
        cancel() {
            this.$emit(`cancel`);
        },
        close(color) {
            if (this.noLivePreview) {
                this.color = color;
            }
            this.pickerEnabled = false;
            this.$emit(`close`, color);
        }
    }
}
</script>

<style lang="scss" scoped>
.input-color {
    &.inline {
        display: inline-flex;
    }
}

.dropdown {
    &.block {
        width: 100%;
    }

    &.simple {
        ::v-deep .btn {
            padding: 0 !important;
            border: none;
        }
    }

    &.button ::v-deep .btn {
        border: var(--border-width) solid var(--border-color);
        display: flex;
        flex-direction: row;
        text-align: left;
        align-items: center;

        .content {
            h4, h5 {
                margin-bottom: 2px;
            }

            padding-left: 10px;
        }
    }

    ::v-deep .dropdown-menu {
        width: 324px;

        .color-palette {
            padding: 0 8px;
        }

        .input-color-preview {
            margin: 2px;
        }
    }
}

.color-button {
    display: inline-flex;
    width: 32px;
    height: 32px;
    border: var(--border-width) solid rgba(black, 0.2);
    border-radius: 4px;
}

.color-picker-dialog {
    padding: 0;
    border: none;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
    border-radius: 8px;

    .vc-photoshop {
        font-family: inherit;

        .vc-ps-fields .vc-input__label,
        .vc-ps-fields .vc-input__desc {
            color: #000;
        }
    }
}
</style>
